// @flow
import React, { type Node, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useKeyboardNavigationMode } from '@ta/onboarding.common';
import classNames from '@ta/common.classnames';
import styles from './Modal.css';

// CSS query selector for elements that can receive keyboard focus. This is used to trap focus
// within the modal while it's open.
const focusableSelector = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex="0"]:not([data-skip-focus])',
].join();

type Props = {|
  onClose: ?() => mixed,
  children: Node,
|};

/**
 * A very basic modal used for the Explicit Preferences flow
 */
const Modal = ({ children, onClose = () => {} }: Props) => {
  const isKeyboardNavigationMode = useKeyboardNavigationMode();

  // Prevent page scrolling while the modal is open
  useLayoutEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const { body } = document;
    if (body == null) {
      return undefined;
    }

    const initialOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = initialOverflow;
    };
  }, []);

  const overlay = useRef<?HTMLDivElement>(null);

  /**
   * When keyboard focus is moved before the modal, wrap around to the last focusable element
   */
  const wrapFocusToEnd = useCallback(() => {
    if (overlay.current == null) {
      return;
    }

    const elements = overlay.current.querySelectorAll(focusableSelector);
    if (elements.length > 0) {
      elements[elements.length - 1].focus();
    }
  }, []);

  /**
   * When keyboard focus is moved past the end of the modal, wrap around to the first focusable
   * element
   */
  const wrapFocusToStart = useCallback(() => {
    if (overlay.current == null) {
      return;
    }

    const element = overlay.current.querySelector(focusableSelector);
    if (element != null) {
      element.focus();
    }
  }, []);

  // Close the modal when escape is pressed
  useEffect(() => {
    const keyUp = (evt: KeyboardEvent) => {
      // 27 = Escape
      if (evt.keyCode === 27 && onClose != null) {
        onClose();
      }
    };

    // eslint-disable-next-line no-restricted-globals
    document.addEventListener('keyup', keyUp);

    // eslint-disable-next-line no-restricted-globals
    return () => document.removeEventListener('keyup', keyUp);
  }, [onClose]);

  useEffect(wrapFocusToStart, []);

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  return (
    <div className={styles.modal}>
      <div className={styles.backdrop} onClick={onClose} />
      <div
        className={classNames(styles.overlay, {
          [styles.keyboardMode]: isKeyboardNavigationMode,
        })}
        ref={overlay}
      >
        <div tabIndex={0} onFocus={wrapFocusToEnd} data-skip-focus />
        {children}
        <div tabIndex={0} onFocus={wrapFocusToStart} data-skip-focus />
      </div>
    </div>
  );
};

export default Modal;
